import { useEffect } from 'react'

/**
 * ページ単位で `<title>` と `<meta name="description">` を設定するフック。
 *
 * SPA（react-router-dom）で遷移しても index.html の静的 head が据え置かれるため、
 * ページごとの題名・説明を出したい画面から呼ぶ。react-helmet 等は導入せず、
 * 依存を増やさない最小実装にしている（本フックが扱うのは head の2要素のみ）。
 *
 * アンマウント時はマウント直前の値へ戻す。ページを跨いだときに前ページの
 * title/description が残るのを防ぐため（戻り先が本フックを使わない画面でも同じ）。
 *
 * 注: 配信は Cloudflare Pages の静的ホスティングで SSR を行わないため、本フックは
 * クローラのうち JS を実行するものにしか効かない。OGP 等を厳密に出す必要が生じたら
 * プリレンダリングの導入が別途必要になる。
 */
export function useDocumentMeta(title: string, description?: string): void {
  useEffect(() => {
    const previousTitle = document.title
    document.title = title

    // description は index.html に存在する想定だが、無ければ生成して後で撤去する。
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]')
    let created = false
    if (description !== undefined && !meta) {
      meta = document.createElement('meta')
      meta.setAttribute('name', 'description')
      document.head.appendChild(meta)
      created = true
    }

    const previousDescription = meta?.getAttribute('content') ?? null
    if (description !== undefined && meta) {
      meta.setAttribute('content', description)
    }

    return () => {
      document.title = previousTitle
      if (description === undefined || !meta) return
      if (created) {
        meta.remove()
      } else if (previousDescription !== null) {
        meta.setAttribute('content', previousDescription)
      } else {
        meta.removeAttribute('content')
      }
    }
  }, [title, description])
}
